import { useMutation, useQueryClient } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import useTakerMarkets, { OfferConfig, TakerMarket } from '@/enterprise/data/useTakerMarkets';
import apiClient from '@/lib/apiClient';
import { usePendingOfferPollingStore } from '@/lib/offerStatusPolling/usePendingOfferPollingStore';
import { useRefetchStatsQueries } from '@/lib/serverSentEvents/useServerSideEventListeners';
import { useReporting } from '@/reporting';
import useDataDog from '@/reporting/DataDog';
import { useIntercom } from '@/reporting/Intercom';

export interface ToggleOfferParams {
  supplierDivisionUuid: string;
  marketUuid: string;
  seaUuid?: string;
  makerOrganizationUuid: string;
  frequency?: OfferConfig['frequency'];
  marketPricingType?: 'PRICE_DISCOVERY' | 'BENCHMARK' | 'STATIC';
  isManuallyAwarded?: boolean;
  isActiveExpressAccept?: boolean;
}

async function toggleOffer({
  seaUuid,
  frequency,
  makerOrganizationUuid,
  marketPricingType,
  isManuallyAwarded,
  isActiveExpressAccept,
}: {
  seaUuid?: string;
  frequency?: OfferConfig['frequency'];
  makerOrganizationUuid: string;
  marketPricingType?: 'PRICE_DISCOVERY' | 'BENCHMARK' | 'STATIC';
  isManuallyAwarded?: boolean;
  isActiveExpressAccept?: boolean;
}): Promise<{ status: number }> {
  if (!seaUuid) {
    throw new Error('An offer uuid is required to toggle the offer');
  }

  // manually awarded and express accept offers both use DELETE to remove the offer
  // Express accept always uses DELETE even if awarded count is 0
  if (isManuallyAwarded || isActiveExpressAccept) {
    const response = await apiClient.delete(`api/supplier-experience/offers/${seaUuid}`, {
      searchParams: qs.stringify(
        {
          makerOrganizationUuids: [makerOrganizationUuid],
        } satisfies paths['/offers/{uuid}']['delete']['parameters']['query'],
        { arrayFormat: 'repeat' }
      ),
    });
    return { status: response.status };
  }

  // if frequency is one time use PATCH to toggle status and convert to recurring, paused
  if (frequency === 'ONE_TIME') {
    if (marketPricingType === 'PRICE_DISCOVERY') {
      const response = await apiClient.patch(`api/supplier-experience/offers/price-discovery`, {
        json: {
          groupingKey: 'SUPPLIER_MARKET',
          frequency: 'recurring',
          status: 'paused',
          offers: [
            {
              makerOrganizationUuid,
              uuid: seaUuid,
            },
          ],
        },
      });
      return { status: response.status };
    } else if (marketPricingType === 'BENCHMARK') {
      const response = await apiClient.patch(`api/supplier-experience/offers/benchmark`, {
        json: {
          groupingKey: 'SUPPLIER_MARKET',
          frequency: 'recurring',
          status: 'paused',
          offers: [
            {
              makerOrganizationUuid,
              uuid: seaUuid,
            },
          ],
        },
      });
      return { status: response.status };
    } else if (marketPricingType === 'STATIC') {
      const response = await apiClient.patch(`api/supplier-experience/offers/static`, {
        json: {
          groupingKey: 'SUPPLIER_MARKET',
          frequency: 'recurring',
          status: 'paused',
          offers: [
            {
              makerOrganizationUuid,
              uuid: seaUuid,
            },
          ],
        },
      });
      return { status: response.status };
    }

    const response = await apiClient.delete(`api/supplier-experience/offers/${seaUuid}`, {
      searchParams: qs.stringify(
        {
          makerOrganizationUuids: [makerOrganizationUuid],
        } satisfies paths['/offers/{uuid}']['delete']['parameters']['query'],
        { arrayFormat: 'repeat' }
      ),
    });
    return { status: response.status };
  }

  const response = await apiClient.patch(`api/supplier-experience/offers/${seaUuid}/toggle-status`, {
    searchParams: qs.stringify(
      {
        makerOrganizationUuids: [makerOrganizationUuid],
      } satisfies paths['/offers/{uuid}/toggle-status']['patch']['parameters']['query'],
      { arrayFormat: 'repeat' }
    ),
  });
  return { status: response.status };
}

const useToggleOffer = () => {
  const queryClient = useQueryClient();
  const { data: takerMarkets } = useTakerMarkets();
  const { refetchOneTakerMarket } = useRefetchStatsQueries();
  const { triggerNPSSurvey } = useIntercom();
  const { addError } = useDataDog();
  const { trackEnterpriseEvent } = useReporting();
  const { addPendingMarkets } = usePendingOfferPollingStore();

  return useMutation({
    mutationKey: ['toggleOffer'],
    mutationFn: async ({
      seaUuid,
      frequency,
      makerOrganizationUuid,
      marketPricingType,
      isManuallyAwarded,
      isActiveExpressAccept,
    }: ToggleOfferParams) => {
      return toggleOffer({
        seaUuid,
        frequency,
        makerOrganizationUuid,
        marketPricingType,
        isManuallyAwarded,
        isActiveExpressAccept,
      });
    },
    onSuccess: (result, variables) => {
      const tmMeta = takerMarkets?.find(
        (meta) => meta.marketUuid === variables.marketUuid && meta.takerDivisionUuid === variables.supplierDivisionUuid
      );

      if (tmMeta?.marketUuid && tmMeta?.takerDivisionId) {
        // update the cache with the returned data
        queryClient.setQueryData<TakerMarket[]>(['taker-markets'], (prevTakerMarkets) =>
          (prevTakerMarkets ?? []).map((tm) => {
            const isTogglingOff = tmMeta.offerConfig.isEnabled === true; // config was true on existing tm data (before toggle called)
            if (tm.marketUuid === variables.marketUuid && tm.takerDivisionUuid === variables.supplierDivisionUuid) {
              // At this time, only price discovery offers will reset an expiration date when toggled off
              const preemptivelyResetExpirationDate = isTogglingOff && tmMeta.offerConfig.type === 'PRICE_DISCOVERY';

              // check if one-time offer being turned off, convert to recurring
              const isFrequencyChanged =
                variables.frequency === 'ONE_TIME' &&
                (variables.marketPricingType === 'PRICE_DISCOVERY' ||
                  variables.marketPricingType === 'BENCHMARK' ||
                  variables.marketPricingType === 'STATIC');

              return {
                ...tm,
                offerConfig: {
                  ...tm.offerConfig,
                  isEnabled: !tmMeta.offerConfig.isEnabled,
                  // update frequency to recurring if converted from one-time
                  ...(isFrequencyChanged ? { frequency: 'RECURRING' as const } : {}),
                },
                // clear expiration date when turning off price discovery offers and one-time offers
                disableAfterMarketClearsDate:
                  preemptivelyResetExpirationDate || isFrequencyChanged ? null : tm.disableAfterMarketClearsDate,
              };
            }

            return tm;
          })
        );

        if (result.status === 202) {
          addPendingMarkets([
            {
              marketUuid: tmMeta.marketUuid,
              takerDivisionUuid: tmMeta.takerDivisionUuid,
              makerOrganizationUuid: tmMeta.makerOrganizationUuid,
            },
          ]);
        } else {
          // offer resolved synchronously, refetch immediately
          trackEnterpriseEvent('polling-update::in-time', {
            marketUuid: tmMeta.marketUuid,
            takerDivisionUuid: tmMeta.takerDivisionUuid,
            makerOrganizationUuid: tmMeta.makerOrganizationUuid,
          });
          refetchOneTakerMarket({ marketUuid: tmMeta.marketUuid, takerId: tmMeta.takerDivisionId });
        }
      }

      setTimeout(() => {
        const isEnabled = !tmMeta?.offerConfig.isEnabled;
        if (isEnabled) {
          triggerNPSSurvey();
        }
      }, 3000);
    },
    onError: (error, variables) => {
      addError('useToggleOffer', {
        error,
        variables,
      });
    },
  });
};

export default useToggleOffer;
